import { useState } from "react";
import {
  Box, Stack, Typography, TextField, Button, Alert, Chip, Paper,
  CircularProgress, Divider, Collapse, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { traceDebin, DebinTrace } from "../services/debines";
import { BRAND } from "../styles/theme";

// Explicación en criollo de cada estado Coelsa (best-effort; código crudo si no está).
const ESTADO_EXPL: Record<string, string> = {
  ACREDITADO:     "El comprador aprobó el débito y la plata se acreditó en la cuenta destino.",
  "SIN GARANTIA": "Se acreditó pero SIN garantía de Coelsa: puede revertirse — verificar antes de liberar fondos.",
  INICIADO:       "El DEBIN fue creado y está esperando que el comprador lo apruebe en su banco/billetera.",
  PENDIENTE:      "Esperando la aprobación del comprador.",
  RECHAZADO:      "El comprador (o su banco) rechazó el débito. No se movió plata.",
  VENCIDO:        "Expiró sin que el comprador lo aprobara. No se movió plata.",
  DEVUELTO:       "La acreditación fue devuelta/revertida después de concretarse.",
  ELIMINADO:      "El DEBIN fue dado de baja antes de resolverse.",
};

function fmtTs(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}
function fmtMoney(v: any): string {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(",", "."));
  return isNaN(n) ? String(v ?? "—") : `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2 })}`;
}

interface TimelineEntry { ts: string; titulo: string; detalle: string; color: string }

/**
 * received_payments no es solo BIND: también trae cobros de STP (México) y de
 * CMF. Los textos salen del procesador del cobro (MX#STP → STP, CLABE, RFC).
 */
function rail(tr: any): { banco: string; cuenta: string; laCuenta: string; fiscal: string } {
  const p = String(tr?.procesador || "").toUpperCase();
  if (p.startsWith("MX#")) return { banco: "STP", cuenta: "CLABE", laCuenta: "la CLABE", fiscal: "RFC" };
  if (p.startsWith("AR#CMF")) return { banco: "CMF", cuenta: "CVU", laCuenta: "el CVU", fiscal: "CUIT" };
  return { banco: "BIND", cuenta: "CVU", laCuenta: "el CVU", fiscal: "CUIT" };
}

/** Arma el timeline cronológico desde las 4 fuentes del trace. */
function buildTimeline(t: DebinTrace): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  const c = t.cache?.prd ?? t.cache?.homo;
  if (c?.fecha) {
    out.push({
      ts: String(c.fecha), color: BRAND.primary,
      titulo: "DEBIN creado en Coelsa",
      detalle: `${c.razon_social || c.collector || "El vendedor"} pidió debitar ${fmtMoney(c.importe)} ` +
               `de la cuenta ${c.cbu_destino || "?"} (CUIT comprador ${c.cuit_comprador || "?"}).`,
    });
  }
  for (const a of t.audit ?? []) {
    const okTxt = a.ok ? "Coelsa lo aceptó" : `Coelsa respondió código ${a.codigo || "?"} ${a.descripcion || ""}`;
    out.push({
      ts: String(a.ts || ""), color: a.action === "confirm" ? "#166534" : "#991B1B",
      titulo: a.action === "confirm" ? "Aprobado por operador (Web Admin)" : "Rechazado por operador (Web Admin)",
      detalle: `${a.by || "?"} ${a.action === "confirm" ? "confirmó" : "rechazó"} el débito` +
               `${a.importe ? ` por ${fmtMoney(a.importe)}` : ""} (ambiente ${a.ambiente || "?"}). ${okTxt}.`,
    });
  }
  const ac = t.acreditacion;
  if (ac?.fecha_hora) {
    out.push({
      ts: String(ac.fecha_hora), color: "#166534",
      titulo: "Plata acreditada en Banco CMF",
      detalle: `Movimiento por ${fmtMoney(ac.monto)} en la cuenta ${ac.cuenta || "?"}, ` +
               `comprobante ${ac.comprobante || "?"}. Es la acreditación REAL vista en los movimientos del banco.`,
    });
  }
  if (c?.vencimiento) {
    out.push({
      ts: String(c.vencimiento), color: "#9A6700",
      titulo: "Vencimiento del DEBIN",
      detalle: "Si el comprador no lo aprobaba antes de esta fecha, el DEBIN expiraba solo.",
    });
  }
  // CP-155: cobros por transferencia a CVU/CLABE (BIND, STP o CMF según el procesador).
  for (const tr of t.transferencia_bind ?? []) {
    const r = rail(tr);
    if (tr.fecha) {
      out.push({
        ts: String(tr.fecha), color: BRAND.primary,
        titulo: `Transferencia recibida en ${r.laCuenta} (${r.banco})`,
        detalle: `${tr.origen_nombre || "?"} (${r.fiscal} ${tr.origen_cuit || "?"}) transfirió ${fmtMoney(tr.monto)}${tr.moneda ? ` ${tr.moneda}` : ""} ` +
                 `desde ${tr.origen_cuenta || "?"} a ${r.laCuenta} ${tr.cvu || "?"} de ${tr.collector || "?"}` +
                 `${tr.cliente_nombre || tr.cliente ? ` (cliente ${tr.cliente_nombre || tr.cliente})` : ""}.`,
      });
    }
    // El backend devuelve solo la liquidación que incluyó este cobro y su
    // transferencia de fondos (con fallidas y reintentos), no todo el día.
    for (const l of tr.liquidaciones ?? []) {
      if (!l.fecha) continue;
      const esTransfer = /^transferencia de fondos/i.test(l.invoice ?? "");
      out.push({
        ts: String(l.fecha), color: l.estado === "completed" || l.estado === "credited" ? "#166534" : "#9A6700",
        titulo: esTransfer
          ? `Transferencia de fondos al collector (${l.estado || "?"})`
          : `Liquidación al collector (${l.estado || "?"})`,
        detalle: esTransfer
          ? `Operación ${l.operation_id || "?"}: ${fmtMoney(l.total)}. Paga la liquidación que incluye este cobro.`
          : `Operación ${l.operation_id || "?"}: total ${fmtMoney(l.total)}, neto ${fmtMoney(l.neto)}` +
            `${l.invoice ? ` — ${l.invoice}` : ""}. Es la liquidación que incluye este cobro.`,
      });
    }
  }
  // CP-517: transferencia saliente de Cucuru al collector.
  for (const s of t.salientes ?? []) {
    if (s.fecha) {
      out.push({
        ts: String(s.fecha), color: BRAND.primary,
        titulo: `${s.tipo === "devolucion" ? "Devolución" : "Liquidación"} creada (${s.banco || "?"})`,
        detalle: `${fmtMoney(s.monto)} ${s.moneda || ""} a ${s.destino_titular || s.collector || "?"} ` +
                 `(${s.destino_cuenta || "?"}), operación ${s.operation_id || "?"}.`,
      });
    }
    if (s.fecha_transferencia) {
      out.push({
        ts: String(s.fecha_transferencia), color: SALIENTE_COLOR[s.estado] ?? "#9A6700",
        titulo: `Transferencia ${SALIENTE_ESTADO[s.estado] ?? s.estado ?? "?"}`,
        detalle: s.detalle_banco || "—",
      });
    }
  }
  return out.sort((a, b) => a.ts.localeCompare(b.ts));
}

const SALIENTE_ESTADO: Record<string, string> = {
  completed: "completada", credited: "acreditada", failed: "fallida", in_process: "en proceso",
};
const SALIENTE_COLOR: Record<string, string> = {
  completed: "#166534", credited: "#166534", failed: "#991B1B", in_process: "#9A6700",
};

function Section({ title, caption, children }: { title: string; caption: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>{caption}</Typography>
      {children}
    </Paper>
  );
}

function KV({ data }: { data: Record<string, any> }) {
  return (
    <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "220px 1fr", rowGap: 0.5, m: 0, fontSize: 13 }}>
      {Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
        <Box key={k} sx={{ display: "contents" }}>
          <Box component="dt" sx={{ color: "text.secondary" }}>{k}</Box>
          <Box component="dd" sx={{ m: 0, fontFamily: "monospace", wordBreak: "break-all" }}>{String(v)}</Box>
        </Box>
      ))}
    </Box>
  );
}

function LogGroup({ label, data }: { label: string; data: any }) {
  const [open, setOpen] = useState(false);
  const events: Array<{ t: string; m: string }> = data?.events ?? [];
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton size="small" onClick={() => setOpen(!open)}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{label}</Typography>
        <Chip size="small" label={data?.error ? "error" : `${events.length} línea(s)${data?.truncated ? "+" : ""}`} />
      </Stack>
      <Collapse in={open}>
        {data?.error && <Alert severity="warning" sx={{ my: 1 }}>{data.error}</Alert>}
        {events.map((e, i) => (
          <Box key={i} sx={{ fontFamily: "monospace", fontSize: 11.5, whiteSpace: "pre-wrap",
                             borderLeft: "3px solid #ddd", pl: 1, my: 0.75, wordBreak: "break-all" }}>
            <Box sx={{ color: "text.secondary" }}>{fmtTs(e.t)}</Box>
            {e.m}
          </Box>
        ))}
        {!data?.error && events.length === 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary", pl: 4 }}>
            Sin menciones del ID en la ventana buscada.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
}

export default function MonitoreoIdCoelsa() {
  const [id, setId]           = useState("");
  const [trace, setTrace]     = useState<DebinTrace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  async function buscar() {
    const clean = id.trim().toUpperCase();
    if (!clean) return;
    setLoading(true); setError(null); setTrace(null);
    try {
      setTrace(await traceDebin(clean));
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally { setLoading(false); }
  }

  const c = trace?.cache?.prd ?? trace?.cache?.homo;
  const estado = String(c?.estado ?? "").toUpperCase();
  const timeline = trace ? buildTimeline(trace) : [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Rastreo por ID (Coelsa / BIND / STP / salientes)</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Buscá un DEBIN por su ID Coelsa, <b>un cobro por transferencia por su ID de transacción (BIND, STP)</b> o
        <b> una transferencia saliente</b> (liquidación o devolución) por el ID que dio el banco: CMF, o la clave
        de rastreo / número de orden de STP. Se buscan todas a la vez y se arma el timeline completo con las
        líneas de log que lo mencionan. Las salientes de Bind todavía no se pueden buscar.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField size="small" label="ID Coelsa, BIND, STP, CMF o clave de rastreo" value={id}
          onChange={(e) => setId(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && buscar()}
          sx={{ width: 380 }} inputProps={{ maxLength: 40, style: { fontFamily: "monospace" } }} />
        <Button variant="contained" onClick={buscar} disabled={loading || !id.trim()}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}>
          Rastrear
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {trace && !trace.found_any && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se encontró nada para <b>{trace.id}</b> en las bases (cache Coelsa, acreditaciones,
          auditoría, movimientos, cobros por transferencia, transferencias salientes){Object.values(trace.logs ?? {}).some((g: any) => g?.events?.length) ?
          " — pero hay menciones en los logs, mirá abajo." : " ni en los logs de la ventana buscada."}
          {" "}Puede ser un DEBIN más viejo que la ventana de 30 días del cache — se puede consultar
          directo a Coelsa (BuscarOperacionDebin) por CLI.
        </Alert>
      )}

      {trace && trace.found_any && (
        <>
          {(trace.salientes?.length ?? 0) > 0 && (
            <Alert severity={trace.salientes!.some((s: any) => s.estado === "failed") ? "error" : "success"} sx={{ mb: 2 }}>
              <b>Es una transferencia saliente de Cucuru.</b>{" "}
              {trace.salientes!.map((s: any) =>
                `${s.tipo === "devolucion" ? "Devolución" : "Liquidación"} de ${fmtMoney(s.monto)} ${s.moneda || ""} ` +
                `a ${s.destino_titular || s.collector || "?"} por ${s.banco || "?"}: ${SALIENTE_ESTADO[s.estado] ?? s.estado ?? "?"}`
              ).join(" · ")}
            </Alert>
          )}
          {(trace.transferencia_bind?.length ?? 0) > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <b>Es un cobro por transferencia ({[...new Set(trace.transferencia_bind.map((tr: any) =>
                `${rail(tr).banco}, a ${rail(tr).cuenta}`))].join(" / ")}), no un DEBIN.</b>{" "}
              {trace.transferencia_bind.map((tr: any) =>
                `${fmtMoney(tr.monto)}${tr.moneda ? ` ${tr.moneda}` : ""} de ${tr.origen_nombre || "?"} a ${rail(tr).laCuenta} de ${tr.collector || "?"}`
              ).join(" · ")}
            </Alert>
          )}
          {estado && (
            <Alert severity={estado === "ACREDITADO" ? "success" : estado === "SIN GARANTIA" ? "warning" : "info"} sx={{ mb: 2 }}>
              <b>Estado Coelsa: {estado}.</b>{" "}
              {ESTADO_EXPL[estado] ?? "Código de estado no catalogado — el valor es el que reporta Coelsa."}
            </Alert>
          )}

          <Section title="Timeline" caption="Cronología reconstruida desde las 4 fuentes. Los horarios son los que reporta cada sistema.">
            {timeline.length === 0 && <Typography variant="body2" color="text.secondary">Sin eventos fechados.</Typography>}
            <Stack spacing={0}>
              {timeline.map((e, i) => (
                <Box key={i} sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: e.color, mt: 0.5 }} />
                    {i < timeline.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: "#e0e0e0" }} />}
                  </Box>
                  <Box sx={{ pb: 2.5 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{fmtTs(e.ts)}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{e.titulo}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>{e.detalle}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Section>

          {Object.entries(trace.cache ?? {}).map(([amb, row]: [string, any]) => (
            <Section key={amb} title={`Listado Coelsa — ambiente ${amb} (debines_cache)`}
              caption="La fila del listado de Coelsa que sincroniza el job cada 5 min (ventana de 30 días). Es lo mismo que ves en la página DEBINes.">
              <KV data={{
                estado: row.estado, importe: fmtMoney(row.importe), moneda: row.moneda,
                "creado (fecha)": row.fecha, vencimiento: row.vencimiento,
                "vendedor (razón social)": row.razon_social, "CUIT originante": row.cuit_originante,
                "CBU originante": row.cbu_originante, "CUIT comprador": row.cuit_comprador,
                "CBU/CVU destino del débito": row.cbu_destino, collector: row.collector,
                "última sincronización": fmtTs(row.synced_at),
                ...(row.sin_garantia_alerted_at ? { "alerta SIN GARANTIA enviada": fmtTs(row.sin_garantia_alerted_at) } : {}),
              }} />
            </Section>
          ))}

          {(trace.transferencia_bind ?? []).map((tr: any, i: number) => { const r = rail(tr); return (
            <Section key={i} title={`Transferencia ${r.banco} — cobro de ${tr.collector || "?"} (received_payments)`}
              caption={`La notificación de ${r.banco} que registró el cobro por transferencia a ${r.laCuenta} del collector, tal como lo indexó yolopago.`}>
              <KV data={{
                collector: tr.collector,
                [r.cuenta === "CLABE" ? "CLABE acreditada" : "CVU acreditado"]: tr.cvu,
                "recibida": fmtTs(tr.fecha),
                importe: `${fmtMoney(tr.monto)} ${tr.moneda || ""}`,
                "origen": [tr.origen_nombre, tr.origen_cuit && `${r.fiscal} ${tr.origen_cuit}`].filter(Boolean).join(" — "),
                "cuenta origen": tr.origen_cuenta,
                "cliente": [tr.cliente, tr.cliente_nombre && `(${tr.cliente_nombre})`].filter(Boolean).join(" "),
                vendor: tr.vendor_id,
                "estado interno": tr.status,
                "fecha de negocio": tr.business_date,
                "día de proceso (liquidación)": tr.process_date,
                procesador: tr.procesador,
                payment_id: tr.payment_id,
                "transfer_id interno": tr.transfer_id,
                referencia: tr.referencia,
              }} />
            </Section>
          ); })}

          {(trace.salientes ?? []).map((s: any, i: number) => (
            <Section key={`sal-${i}`} title={`Transferencia saliente — ${s.tipo === "devolucion" ? "devolución" : "liquidación"} de ${s.collector || "?"} (ar_operations)`}
              caption="La operación con la que Cucuru le transfiere al collector (o devuelve un cobro rechazado), tal como la registró el proceso de liquidación.">
              <KV data={{
                collector: s.collector,
                estado: SALIENTE_ESTADO[s.estado] ? `${SALIENTE_ESTADO[s.estado]} (${s.estado})` : s.estado,
                banco: s.banco,
                importe: `${fmtMoney(s.monto)} ${s.moneda || ""}`,
                "creada": fmtTs(s.fecha),
                "transferida": s.fecha_transferencia ? fmtTs(s.fecha_transferencia) : null,
                "fecha de liquidación": s.payment_date,
                "destino": [s.destino_titular, s.destino_cuit && `CUIT/RFC ${s.destino_cuit}`].filter(Boolean).join(" — "),
                "cuenta destino": s.destino_cuenta,
                "cuenta origen": s.origen_cuenta,
                "operation_id": s.operation_id,
                "transfer_id (banco)": s.transfer_id,
                "clave de rastreo (STP)": s.clave_rastreo,
                "orden STP": s.orden_stp,
                "estado orden STP": s.orden_estado,
                concepto: s.concepto,
                "respuesta del banco": s.detalle_banco,
              }} />
            </Section>
          ))}

          {trace.acreditacion && (
            <Section title="Acreditación bancaria (debin_acreditaciones)"
              caption="Índice que arma el job debin-index leyendo los movimientos reales de Banco CMF: cuándo entró efectivamente la plata. Es la fuente del comprobante PDF.">
              <KV data={{
                "fecha/hora de acreditación": trace.acreditacion.fecha_hora,
                monto: fmtMoney(trace.acreditacion.monto),
                comprobante: trace.acreditacion.comprobante,
                "cuenta CMF": trace.acreditacion.cuenta,
                concepto: trace.acreditacion.concepto,
                "indexado el": fmtTs(trace.acreditacion.indexed_at),
              }} />
            </Section>
          )}

          {(trace.audit?.length ?? 0) > 0 && (
            <Section title="Acciones de operadores (webadmin2-debin-audit)"
              caption="Quién confirmó/rechazó este DEBIN desde el Web Admin y qué respondió Coelsa.">
              {trace.audit.map((a: any, i: number) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <Chip size="small" label={a.action} color={a.action === "confirm" ? "success" : "error"} sx={{ mr: 1 }} />
                  <Typography component="span" variant="body2">
                    {fmtTs(a.ts)} — <b>{a.by}</b> (ambiente {a.ambiente}, importe {fmtMoney(a.importe)}) →{" "}
                    {a.ok ? "OK" : `código ${a.codigo || "?"} ${a.descripcion || ""}`}
                  </Typography>
                </Box>
              ))}
            </Section>
          )}

          {(trace.movimientos?.length ?? 0) > 0 && (
            <Section title="Movimiento bancario crudo (cmf_movimientos)"
              caption="La línea tal cual aparece en el extracto de Banco CMF (copia local que mantiene el job).">
              {trace.movimientos.map((m: any, i: number) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <KV data={{
                    fecha_hora: m.fecha_hora, concepto: m.concepto, detalle: m.detalle,
                    comprobante: m.comprobante, monto: fmtMoney(m.monto),
                    saldo_parcial: m.saldo_parcial, cuenta: m.cuenta,
                  }} />
                  {i < trace.movimientos.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                </Box>
              ))}
            </Section>
          )}
        </>
      )}

      {trace && (
        <Section title="Logs (CloudWatch)"
          caption={`Líneas que mencionan el ID en los logs del Web Admin y del connector Coelsa. Ventana buscada: ${fmtTs(trace.logs?.window?.from)} → ${fmtTs(trace.logs?.window?.to)} (acotada a las fechas conocidas del DEBIN).`}>
          <LogGroup label="webadmin2 (billing: sync, confirmaciones, jobs)" data={trace.logs?.webadmin2} />
          <LogGroup label="CoelsaConnectorProd (llamadas a la API de Coelsa)" data={trace.logs?.coelsa_connector} />
        </Section>
      )}
    </Box>
  );
}
